import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download } from 'lucide-react'
import RadarChart from './RadarChart'
import AssessmentSelector from './AssessmentSelector'
import useAssessment from '../hooks/useAssessment'
import { buildComparePath, assessmentApiUrl } from '../utils/adminLinks'

const AssessmentDetail = ({ assessment: initialAssessment }) => {
  const navigate = useNavigate()
  const { downloadPDF, isSubmitting: isExporting } = useAssessment()
  const [assessment, setAssessment] = useState(initialAssessment)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [exportError, setExportError] = useState(null)
  const [showCompareModal, setShowCompareModal] = useState(false)

  useEffect(() => {
    if (initialAssessment && initialAssessment.id) {
      fetchFullAssessment(initialAssessment.id)
    }
  }, [initialAssessment])

  const fetchFullAssessment = async (id) => {
    try {
      setLoading(true)
      const token = localStorage.getItem('adminToken')
      
      const response = await fetch(assessmentApiUrl(id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAssessment(data.data)
      } else {
        throw new Error('Erreur lors du chargement des détails')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100'
    if (score >= 6) return 'text-yellow-600 bg-yellow-100'
    if (score >= 4) return 'text-orange-600 bg-orange-100'
    return 'text-red-600 bg-red-100'
  }

  const getScoreLevel = (score) => {
    if (score >= 8) return 'Excellent'
    if (score >= 6) return 'Bon'
    if (score >= 4) return 'Moyen'
    return 'Faible'
  }

  const handleCompareWith = (selectedIds) => {
    setShowCompareModal(false)
    navigate(buildComparePath([assessment.id, ...selectedIds]))
  }

  const handleExportPDF = async () => {
    try {
      setExportError(null)
      await downloadPDF(assessment.clientInfo, assessment.assessmentData)
    } catch (err) {
      setExportError('Échec de la génération du PDF. Veuillez réessayer.')
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement des détails...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg">
        <h3 className="font-medium">Erreur</h3>
        <p>{error}</p>
      </div>
    )
  }

  if (!assessment || !assessment.assessmentData) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-600">Aucune donnée d'évaluation disponible</p>
      </div>
    )
  }

  // Prepare radar chart data
  const radarData = assessment.assessmentData.themes?.map(theme => ({
    name: theme.title,
    shortName: theme.title.substring(0, 15),
    score: theme.questions ? 
      theme.questions.reduce((sum, q) => {
        const score = assessment.assessmentData.scores[q.id] || 0
        return sum + score
      }, 0) / theme.questions.length : 0
  })) || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {assessment.clientInfo.firstName} {assessment.clientInfo.lastName}
            </h2>
            <p className="text-lg text-gray-600 mt-1">
              {assessment.clientInfo.company}
            </p>
            <div className="flex flex-wrap items-center mt-2 gap-x-4 gap-y-2">
              <span className="text-sm text-gray-500">
                ID: {assessment.reportId}
              </span>
              <span className="text-sm text-gray-500">
                Évaluation: {formatDate(assessment.clientInfo.assessmentDate)}
              </span>
              <span className="text-sm text-gray-500">
                Soumission: {formatDate(assessment.storedAt)}
              </span>
              <span className="text-sm text-gray-500 flex items-center">
                Version: 
                {assessment.questionnaireVersion ? (
                  <span className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    v{assessment.questionnaireVersion}
                  </span>
                ) : (
                  <span className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" title="Évaluation créée avant le système de versioning">
                    legacy
                  </span>
                )}
              </span>
              <button
                onClick={() => setShowCompareModal(true)}
                className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium border border-blue-200 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Comparer avec...
              </button>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="text-green-600 hover:text-green-800 flex items-center text-sm font-medium border border-green-200 px-3 py-1 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-1.5" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1.5" />
                    Exporter PDF
                  </>
                )}
              </button>
            </div>
            {exportError && (
              <p className="text-xs text-red-600 mt-1">{exportError}</p>
            )}
          </div>
          <div className="mt-4 lg:mt-0 lg:ml-6">
            <div className="text-center">
              <div className={`inline-flex px-4 py-2 text-2xl font-bold rounded-lg ${getScoreColor(assessment.assessmentData.averageScore)}`}>
                {assessment.assessmentData.averageScore.toFixed(1)}/10
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {getScoreLevel(assessment.assessmentData.averageScore)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Informations client
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Nom complet</label>
            <p className="text-sm text-gray-900">{assessment.clientInfo.firstName} {assessment.clientInfo.lastName}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Email</label>
            <p className="text-sm text-gray-900">
              <a href={`mailto:${assessment.clientInfo.email}`} className="text-blue-600 hover:text-blue-800">
                {assessment.clientInfo.email}
              </a>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Entreprise</label>
            <p className="text-sm text-gray-900">{assessment.clientInfo.company}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">N° Dossier</label>
            <p className="text-sm text-gray-900">{assessment.clientInfo.clientId || 'Non renseigné'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Téléphone</label>
            <p className="text-sm text-gray-900">
              <a href={`tel:${assessment.clientInfo.phone}`} className="text-blue-600 hover:text-blue-800">
                {assessment.clientInfo.phone}
              </a>
            </p>
          </div>
          {assessment.clientInfo.consultantEmail && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Email Consultant</label>
              <p className="text-sm text-gray-900">
                <a href={`mailto:${assessment.clientInfo.consultantEmail}`} className="text-blue-600 hover:text-blue-800">
                  {assessment.clientInfo.consultantEmail}
                </a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Radar Chart */}
      {radarData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Profil des scores par thème
          </h3>
          <div className="h-96">
            <RadarChart 
              domains={radarData}
              scores={radarData.map(d => d.score)}
            />
          </div>
        </div>
      )}

      {/* Themes and Questions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Détail par thème ({assessment.assessmentData.completedQuestions}/{assessment.assessmentData.totalQuestions} questions répondues)
        </h3>
        
        <div className="space-y-6">
          {assessment.assessmentData.themes?.map((theme, themeIndex) => {
            const themeScores = theme.questions?.map(q => assessment.assessmentData.scores[q.id]).filter(score => score !== undefined) || []
            const themeAverage = themeScores.length > 0 ? themeScores.reduce((sum, score) => sum + score, 0) / themeScores.length : 0
            
            return (
              <div key={theme.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{theme.icon}</span>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{theme.title}</h4>
                      <p className="text-sm text-gray-600">{theme.questions?.length || 0} questions</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getScoreColor(themeAverage)}`}>
                      {themeAverage.toFixed(1)}/10
                    </span>
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-3">
                  {theme.questions?.map((question, questionIndex) => {
                    const score = assessment.assessmentData.scores[question.id]
                    
                    return (
                      <div key={question.id} className="bg-gray-50 rounded p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-gray-900 mb-1">
                              {question.title}
                            </h5>
                            <p className="text-xs text-gray-600">
                              {question.description}
                            </p>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            {score !== undefined ? (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getScoreColor(score)}`}>
                                {score}/10
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs text-gray-500 bg-gray-200 rounded">
                                Non répondu
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Technical Information */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Informations techniques</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600">
          <div>
            <span className="font-medium">ID Rapport:</span> {assessment.reportId}
          </div>
          <div>
            <span className="font-medium">Généré le:</span> {formatDate(assessment.generatedAt)}
          </div>
          <div>
            <span className="font-medium">Stocké le:</span> {formatDate(assessment.storedAt)}
          </div>
        </div>
      </div>

      {/* Assessment Selector Modal */}
      {showCompareModal && (
        <AssessmentSelector
          currentAssessmentId={assessment.id}
          currentClientId={assessment.clientInfo.clientId}
          onSelect={handleCompareWith}
          onClose={() => setShowCompareModal(false)}
        />
      )}
    </div>
  )
}

export default AssessmentDetail
